import { useState } from "react";
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  Divider,
  InlineStack,
  Modal,
  Select,
  Text,
  TextField,
  Tooltip,
} from "@shopify/polaris";
import { ReceiptIcon } from "@shopify/polaris-icons";
import supabase from "../../supabase-client";
import SpeiPaymentModal from "../Services/SpeiPaymentModal";
import { speiEnabled, getPayoutReceipt } from "../../utility/pagos";
import { formatPrice, formatFriendlyDate } from "../../utility/funcs";

// What the trasladista is owed for this traslado, and the settlements that pay
// it. The first one is the anticipo — chofer + comida, payable as soon as the
// traslado is authorized so the trasladista has money for gas and tolls; later
// ones pay approved tickets. Amounts here are only a preview: the settlement is
// computed in create_traslado_settlement. Anything agreed after the anticipo
// (extra days, extra comida…) is a "pago adicional": its own settlement from
// create_traslado_extra_settlement, with the reason in extra_reason.

const SETTLEMENT_STATUS = {
  pending_payment: { label: "Por pagar", tone: "attention" },
  paid: { label: "Pagado", tone: "success" },
  cancelled: { label: "Cancelado", tone: undefined },
};

function TrasladoSettlementCard({ order, tickets, settlements, canPay, onChange }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(null); // settlement being marked paid
  const [speiFor, setSpeiFor] = useState(null); // settlement being paid by SPEI
  const [receiptFor, setReceiptFor] = useState(null);

  // Fintoc's own PDF: a signed URL that expires in ~1h, so it's fetched on
  // click and never stored (same as provider payments).
  const openReceipt = async (s) => {
    setReceiptFor(s.id);
    setError(null);
    try {
      const { download_url } = await getPayoutReceipt(s.payout_id);
      window.open(download_url, "_blank");
    } catch (err) {
      setError(err.message || "No se pudo obtener el comprobante de Fintoc");
    } finally {
      setReceiptFor(null);
    }
  };
  const [ref, setRef] = useState("");
  const [extra, setExtra] = useState(null); // { trasladista, chofer, comida, reason }

  const live = settlements.filter((s) => s.status !== "cancelled");
  const first = live.length === 0;
  const sum = (arr) => arr.reduce((a, t) => a + Number(t.total || 0), 0);
  const approvedNew = tickets.filter((t) => t.status === "approved" && !t.settlement_id);
  const review = tickets.filter((t) => t.status === "needs_review");
  const waitingCfdi = tickets.filter((t) => t.status === "pending_cfdi" || t.status === "cfdi_matched");
  const lost = tickets.filter((t) => t.status === "expired" || t.status === "rejected");

  // First settlement: the secondary trasladista's fixed share of the chofer
  // pay goes to them as its own settlement; the main one gets the rest.
  const second = first && order.trasladista_secondary ? Number(order.secondary_amount || 0) : 0;
  const chofer = first ? Number(order.driver_extra || 0) - second : 0;
  const names = {
    [order.trasladista?.id]: order.trasladista?.name,
    [order.secondary?.id]: order.secondary?.name,
  };
  const comida = first ? Number(order.estimate?.comida || 0) : 0;
  const now = chofer + second + comida + sum(approvedNew);
  const PAYABLE = ["authorized", "in_progress", "completed", "settled"];
  const delivered = ["completed", "settled"].includes(order.status);
  const canSettle = canPay && PAYABLE.includes(order.status) && now > 0;

  const settle = async () => {
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.rpc("create_traslado_settlement", { p_order: order.id });
    setBusy(false);
    if (err) setError(err.message);
    else onChange();
  };

  const createExtra = async () => {
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.rpc("create_traslado_extra_settlement", {
      p_order: order.id,
      p_trasladista: Number(extra.trasladista),
      p_chofer: Number(extra.chofer || 0),
      p_comida: Number(extra.comida || 0),
      p_reason: extra.reason,
    });
    setBusy(false);
    if (err) {
      setError(err.message);
      return;
    }
    setExtra(null);
    onChange();
  };
  const extraTotal = extra ? Number(extra.chofer || 0) + Number(extra.comida || 0) : 0;
  const canExtra = canPay && PAYABLE.includes(order.status) && !first;

  const markPaid = async () => {
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.rpc("mark_traslado_settlement_paid", {
      p_settlement: paying.id,
      p_ref: ref,
    });
    setBusy(false);
    if (err) {
      setError(err.message);
      return;
    }
    setPaying(null);
    setRef("");
    onChange();
  };

  const row = (label, value, tone) => (
    <InlineStack align="space-between">
      <Text tone={tone}>{label}</Text>
      <Text tone={tone}>{value}</Text>
    </InlineStack>
  );

  // Nothing to pay before the traslado is authorized.
  if (!PAYABLE.includes(order.status) && !settlements.length) return null;

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <Text variant="headingMd">Liquidación</Text>
          {canExtra && (
            <Button
              variant="plain"
              onClick={() =>
                setExtra({ trasladista: String(order.trasladista?.id ?? ""), chofer: "", comida: "", reason: "" })
              }
            >
              Pago adicional
            </Button>
          )}
        </InlineStack>
        {error && <Banner tone="critical">{error}</Banner>}

        {now > 0 && (
          <BlockStack gap="100">
            <Text fontWeight="semibold">
              {first ? "Anticipo (chofer + comida)" : "Nuevo por liquidar"}
            </Text>
            {first &&
              row(
                second > 0 ? `Chofer · ${order.trasladista?.name || "principal"}` : "Chofer",
                formatPrice(chofer),
              )}
            {first && second > 0 &&
              row(`Chofer · ${order.secondary?.name || "secundario"} (secundario)`, formatPrice(second))}
            {first && comida > 0 && row("Comida", formatPrice(comida))}
            {approvedNew.length > 0 &&
              row(`Tickets aprobados (${approvedNew.length})`, formatPrice(sum(approvedNew)))}
            {approvedNew.some((t) => t.deductible === false) &&
              row(
                "de ellos, sin factura (no deducible)",
                formatPrice(sum(approvedNew.filter((t) => t.deductible === false))),
                "subdued",
              )}
            <InlineStack align="space-between" blockAlign="center">
              <Text fontWeight="bold">Total</Text>
              <InlineStack gap="200" blockAlign="center">
                <Text fontWeight="bold">{formatPrice(now)}</Text>
                {canSettle && (
                  <Button variant="primary" loading={busy} onClick={settle}>
                    {first && !delivered ? "Pagar anticipo" : "Liquidar"}
                  </Button>
                )}
              </InlineStack>
            </InlineStack>
          </BlockStack>
        )}

        {(review.length > 0 || waitingCfdi.length > 0 || lost.length > 0) && (
          <BlockStack gap="100">
            {review.length > 0 &&
              row(`Por revisar (${review.length})`, formatPrice(sum(review)), "caution")}
            {waitingCfdi.length > 0 &&
              row(`Esperando factura (${waitingCfdi.length})`, formatPrice(sum(waitingCfdi)), "subdued")}
            {lost.length > 0 &&
              row(`Vencidos o rechazados (${lost.length})`, formatPrice(sum(lost)), "subdued")}
            {(review.length > 0 || waitingCfdi.length > 0) && (
              <Text tone="subdued" variant="bodySm">
                Se podrán liquidar aparte cuando se aprueben.
              </Text>
            )}
          </BlockStack>
        )}

        {settlements.length > 0 && (
          <>
            <Divider />
            {settlements.map((s) => (
              <InlineStack key={s.id} align="space-between" blockAlign="center">
                <BlockStack gap="0">
                  <InlineStack gap="200" align="start" blockAlign="center">
                    <Text fontWeight="semibold">{formatPrice(s.total)}</Text>
                    {s.extra_reason ? (
                      <Badge tone="info">Pago adicional</Badge>
                    ) : (
                      Number(s.amount_chofer) > 0 && <Badge>Anticipo</Badge>
                    )}
                    <Badge tone={SETTLEMENT_STATUS[s.status]?.tone}>
                      {SETTLEMENT_STATUS[s.status]?.label}
                    </Badge>
                    {s.status === "paid" && s.payout_id && (
                      <Tooltip content="Descargar comprobante de Fintoc">
                        <Button
                          size="slim"
                          variant="tertiary"
                          icon={ReceiptIcon}
                          accessibilityLabel="Descargar comprobante de Fintoc"
                          loading={receiptFor === s.id}
                          onClick={() => openReceipt(s)}
                        />
                      </Tooltip>
                    )}
                  </InlineStack>
                  <Text tone="subdued" variant="bodySm">
                    {[
                      names[s.trasladista],
                      formatFriendlyDate(s.created_at),
                      Number(s.amount_chofer) > 0 && `chofer ${formatPrice(s.amount_chofer)}`,
                      Number(s.amount_comida) > 0 && `comida ${formatPrice(s.amount_comida)}`,
                      Number(s.amount_tickets) > 0 && `tickets ${formatPrice(s.amount_tickets)}`,
                      s.extra_reason,
                      s.payment_ref && `ref. ${s.payment_ref}`,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </Text>
                  {s.status === "pending_payment" && s.returned_at && (
                    <Text tone="critical" variant="bodySm">
                      {`SPEI devuelto (${s.returned_reason || "sin motivo"}). Revisa la cuenta del trasladista antes de reintentar.`}
                    </Text>
                  )}
                </BlockStack>
                {s.status === "pending_payment" && canPay && (
                  <InlineStack gap="200" wrap={false}>
                    {speiEnabled() && (
                      <Button size="slim" variant="primary" onClick={() => setSpeiFor(s)}>
                        Pagar por SPEI
                      </Button>
                    )}
                    <Button size="slim" variant="plain" onClick={() => setPaying(s)}>
                      Marcar pagado
                    </Button>
                  </InlineStack>
                )}
              </InlineStack>
            ))}
          </>
        )}
      </BlockStack>

      {speiFor && (
        <SpeiPaymentModal
          open
          payableKind="traslado_settlement"
          payableId={speiFor.id}
          nounLabel="liquidación"
          beneficiaryLabel="trasladista"
          onClose={() => {
            setSpeiFor(null);
            onChange();
          }}
          onAuthorized={() => onChange()}
        />
      )}

      <Modal
        open={!!extra}
        onClose={() => setExtra(null)}
        title="Pago adicional"
        primaryAction={{
          content: extraTotal > 0 ? `Crear por ${formatPrice(extraTotal)}` : "Crear",
          loading: busy,
          disabled: !extra || !(extraTotal > 0) || !extra.reason.trim() || !extra.trasladista,
          onAction: createExtra,
        }}
        secondaryActions={[{ content: "Cancelar", onAction: () => setExtra(null) }]}
      >
        {extra && (
          <Modal.Section>
            <BlockStack gap="300">
              {order.secondary && (
                <Select
                  label="Trasladista"
                  options={[
                    { label: order.trasladista?.name || "Principal", value: String(order.trasladista?.id) },
                    { label: `${order.secondary.name} (secundario)`, value: String(order.secondary.id) },
                  ]}
                  value={extra.trasladista}
                  onChange={(v) => setExtra({ ...extra, trasladista: v })}
                />
              )}
              <InlineStack gap="300" wrap={false}>
                <TextField
                  label="Chofer"
                  type="number"
                  prefix="$"
                  autoComplete="off"
                  value={extra.chofer}
                  onChange={(v) => setExtra({ ...extra, chofer: v })}
                />
                <TextField
                  label="Comida"
                  type="number"
                  prefix="$"
                  autoComplete="off"
                  value={extra.comida}
                  onChange={(v) => setExtra({ ...extra, comida: v })}
                />
              </InlineStack>
              <TextField
                label="Motivo"
                autoComplete="off"
                placeholder="Ej. 2 días extra en Cancún"
                value={extra.reason}
                onChange={(v) => setExtra({ ...extra, reason: v })}
              />
              <Text tone="subdued" variant="bodySm">
                Queda por pagar como una liquidación más; se paga por SPEI desde aquí.
              </Text>
            </BlockStack>
          </Modal.Section>
        )}
      </Modal>

      <Modal
        open={!!paying}
        onClose={() => setPaying(null)}
        title={paying ? `Marcar pagado ${formatPrice(paying.total)}` : ""}
        primaryAction={{
          content: "Marcar pagado",
          loading: busy,
          disabled: ref.trim().length < 4,
          onAction: markPaid,
        }}
        secondaryActions={[{ content: "Cancelar", onAction: () => setPaying(null) }]}
      >
        <Modal.Section>
          <TextField
            label="Referencia o clave de rastreo del SPEI"
            autoComplete="off"
            value={ref}
            onChange={setRef}
          />
        </Modal.Section>
      </Modal>
    </Card>
  );
}

export default TrasladoSettlementCard;
